'use client'
import { LinkExternal } from '@/types/common/LinkExternal'
import { Image } from '@/types/common/ImageData'
import s from './Content.module.scss'
import { useContext, useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import React from 'react'
import LazyImage from '@/components/common/LazyImage'
import { Body } from '@/types/common/Body'
import { PortableText } from '@portabletext/react'
import { portableBlockComponents } from '@/utils/portableText'
import CreditsOnBottomComponent from '../creditsOnBottom'
import Gallery from '@/sanity/schemas/objects/modulesJournals/gallery'
import GalleryProjectComponent from '../gallery'
import { WebContext } from '@/context/webContext'
import RelatedComponent from '@/components/modulesJournal/related'
import usePinShorterColumn from '@/utils/usePinShorterColumn'

interface CreditsProps {
    _type: string;
    _ref: string;
    credits: Array<Credit>;
}

interface Credit {
    title: string;
    names: Body[] | null;
}

interface ContentProps {
    content: Body[] | null;
    gallery: Array<GalleryProps>;
    galleryHidden: Array<GalleryProps> | null;
    credits: CreditsProps;
    title: string;
    related: any;
}

interface GalleryProps {
    hide: boolean | null;
    imageUrl: string;
    metadata: {
        dimensions: {
            width: number;
            height: number;
        }
        lqip?: string;
    }
    ref: string;
    attribution: string;
    caption: any;
    filename: string;
}

const hasFootnotes = (content: any): boolean => {
    // Verificar si content está definido y es un array
    if (!Array.isArray(content)) {
        return false;
    }

    // Buscar footnotes en el contenido
    return content.some((block: any) => {
        if (block._type === "block" && block.markDefs) {
            return block.markDefs.some((def: any) => def._type === "footnote");
        }
        return false;
    });
};

export default function ContentComponent({
    content, gallery, galleryHidden, credits, title, related
}: ContentProps) {

    const {
        colorHeader,
        setColorHeader,
        backgroundHeader,
        setBackgroundHeader,
        openOverview,
        setOpenOverview,
        pastColorHeader,
        setPastColorHeader,
        pastBackgroundHeader,
        setPastBackgroundHeader
    } = useContext(WebContext)

    const hasAnyFootnotes = hasFootnotes(content);

    const textRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const galleryRef = useRef<HTMLDivElement>(null);

    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);

    const [isMobile, setIsMobile] = useState(true);

    const [expanded, setExpanded] = useState(false);
    const readMoreRef = useRef<HTMLButtonElement>(null);

    const handleExpandClick = () => {
        const textEl = textRef.current;
        const buttonEl = readMoreRef.current;

        if (!textEl || !buttonEl) return;

        const scrollYBefore = window.scrollY;
        const previousHeight = textEl.offsetHeight;
        const wasExpanded = expanded;

        setExpanded(!wasExpanded);

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const newHeight = textEl?.offsetHeight ?? previousHeight;

                if (!wasExpanded) {
                    // ✅ Expanding: keep scrollY the same
                    window.scrollTo({
                        top: scrollYBefore,
                        behavior: 'auto',
                    });
                } else {
                    // ✅ Collapsing: scroll to the button, with a 30% viewport height offset
                    const buttonTop = buttonEl.getBoundingClientRect().top + window.scrollY;
                    const offset = window.innerHeight * 0.3;

                    window.scrollTo({
                        top: buttonTop - offset,
                        behavior: 'smooth',
                    });
                }
            });
        });
    };

    // CSS
    const mobileTextClass = expanded ? s.expanded : s.collapsed;

    useEffect(() => {
        const checkScreenSize = () => setIsMobile(window.innerWidth < 768);

        checkScreenSize();
        window.addEventListener("resize", checkScreenSize);

        return () => window.removeEventListener("resize", checkScreenSize);
    }, []);

    usePinShorterColumn(textRef, galleryRef);

    useEffect(() => {
        if (!isGalleryOpen) return;
        document.body.style.overflow = 'hidden';
        // Cleanup: restaura el scroll también si se navega con el lightbox abierto
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [isGalleryOpen]);

    useEffect(() => {

        if (openOverview) {
            setIsGalleryOpen(true);
            setPastColorHeader(colorHeader);
            setPastBackgroundHeader(backgroundHeader);
            setBackgroundHeader(true);
            setColorHeader('black');
        }

    }, [openOverview])

    const handleImageClick = (index: number) => {
        const selectedImage = gallery[index];

        // El lightbox usa galleryHidden cuando existe; localizamos allí la imagen clicada.
        // Si galleryHidden no existe, el lightbox usa la galería visible y el índice ya es el correcto.
        if (galleryHidden && galleryHidden.length > 0) {
            const hiddenIndex = galleryHidden.findIndex(img => img.imageUrl === selectedImage.imageUrl);
            setSelectedImageIndex(hiddenIndex !== -1 ? hiddenIndex : Math.min(index, galleryHidden.length - 1));
        } else {
            setSelectedImageIndex(index);
        }

        setIsGalleryOpen(true);
        setPastColorHeader(colorHeader);
        setPastBackgroundHeader(backgroundHeader);
        setBackgroundHeader(true);
        setColorHeader('black');
    };

    // Función para manejar el clic en el botón "OVERVIEW"
    const handleOverviewClick = () => {
        setOpenOverview(true); // Abre la galería en modo "overview"
        setPastColorHeader(colorHeader);
        setPastBackgroundHeader(backgroundHeader);
        setTimeout(() => {
            setColorHeader('black'); // Cambia el color del texto del header a negro
            setBackgroundHeader(true); // Cambia el fondo del header a blanco
        }, 100);

    };

    return (
        <>
            <div ref={containerRef} className={`${s.sideText} ${hasAnyFootnotes ? s.footnotes : ""}`}>
                <div ref={textRef} className={`${s.text} ${isMobile ? mobileTextClass : ''}`}>
                    {content &&
                        <PortableText
                            value={content}
                            components={portableBlockComponents(content)}
                        />
                    }
                    <div className={s.credits}>
                        <CreditsOnBottomComponent data={credits} />
                    </div>

                </div>
                <>{isMobile && (
                    <button className={s.readMoreButton} ref={readMoreRef} onClick={handleExpandClick}>
                        {expanded ? "Read Less" : "Read More"}
                    </button>
                )}</>
                <div ref={galleryRef} className={`${s.gallery} ${hasAnyFootnotes ? s.footnotes : s.noFootnotes}`}>
                    {gallery && gallery.map((image, index) => {
                        return (
                            <div key={index} className={s.image} onClick={() => handleImageClick(index)}>
                                <LazyImage
                                    src={image.imageUrl}
                                    width={image.metadata.dimensions.width}
                                    height={image.metadata.dimensions.height}
                                    lqip={image.metadata.lqip || ""}
                                    sizes="(max-width: 768px) 100vw, 60vw"
                                    fill={false}
                                    alt={image.attribution || ""}
                                    caption={image.caption || null}
                                    filename={image.filename || ""}
                                />
                            </div>
                        )
                    })}
                </div>
                <div className={s.creditsMobile}>
                    <CreditsOnBottomComponent data={credits} />
                </div>
                <div className={s.overviewButton} onClick={handleOverviewClick}>
                    <p>OVERVIEW</p>
                </div>
            </div>
            {related && related.length > 0 && related.map((item: any, index: number) => (
                <section className={s.sectionRelated} key={`related-${index}`}>
                    <RelatedComponent data={item} />
                </section>
            ))}

            {isGalleryOpen && (
                <GalleryProjectComponent
                    gallery={gallery}
                    galleryHidden={galleryHidden}
                    title={title}
                    initialIndex={selectedImageIndex}
                    onClose={() => {
                        setIsGalleryOpen(false);
                        setOpenOverview(false);
                        setColorHeader(pastColorHeader);
                        setBackgroundHeader(pastBackgroundHeader);
                    }}
                />
            )}
        </>
    )
}